import React, { RefObject, createRef, useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import clsx from 'classnames';
import { ReactSVG } from 'react-svg';
import { Button } from '@shopee/chatbot-ui';

import {
  TRACKING_MSG_TYPE,
  TRACKING_TYPE,
  INTENT_HOT_QUESTION_DISPLAY_TYPE,
  TRACKING_SECTION,
  TRACKING_TARGET,
} from '@/utils/constants';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import handleClickIntent from '@/utils/intent/handleClickIntent';
import sendBotMessageToAgent from '@/utils/sendBotMessageToAgent';
import HotQuestionRightArrow from '@/assets/img/hot_question_right_arrow.svg';
import { InitStoreType } from '@/types/store/init';
import { IntentType, IntentSource } from '@/types/intent';
import { trackingHandler } from '@/utils/btnClickHandler';
import { getChildInject } from '@/utils/instantLoaded/tools';
import { agentStore } from '@/stores';
import { getTrans, handleClickHistoryButton } from '@/utils/tools';
import { updateDialogueInfo } from '@/utils/sessionUtils';
import { ChatbotUIBasicProps } from '@/types/common';
import { isUIOptimisationHotQuestion } from '@/utils/common/UIOptimisation/HotQuestion';
import handleClickOtherFrequentlyAsked from '@/utils/intent/handleClickOtherFrequentlyAsked';
import IconDown from '@/assets/img/ic-down-orange.svg';
import UiOptimisationRecommenderRightArrow from '@/assets/img/ui-optimisation-recommender/ui-optimisation-recommender-right-arrow.svg';
import OtherFrequentlyAsked from '@/assets/img/other-frequently-asked.svg';

import Msg from '../Msg';

import './style.scss';

const PREFIX = 'new-hot-question-wrapper';

export const MINIMISE_CATEGORY_UI_ITEM_PREFIX = 'minimise-category-ui-question-item';

export function MinimiseCategoryUIQuestionItem(props: {
  text: string;
  isOtherFrequentlyAsked?: boolean;
  adhocCategoryName?: string;
}) {
  const { text, isOtherFrequentlyAsked, adhocCategoryName } = props;
  return (
    <div className={`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-container`}>
      <div
        className={clsx(`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-content`, { [`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-other-frequently-asked-content`]: isOtherFrequentlyAsked })}
      >
        {isOtherFrequentlyAsked && (
          <ReactSVG
            src={OtherFrequentlyAsked}
            className={`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-other-frequently-asked-icon`}
          />
        )}
        <span
          className={clsx(`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-text`, { [`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-other-frequently-asked-text`]: isOtherFrequentlyAsked })}
        >
          {text}
        </span>
      </div>

      <div
        className={clsx(`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-icon-container`, { [`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-other-frequently-asked-icon-container`]: isOtherFrequentlyAsked })}
      >
        {adhocCategoryName && (
          <div className={`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-adhoc-category`}>{adhocCategoryName}</div>
        )}
        <ReactSVG
          src={UiOptimisationRecommenderRightArrow}
          className={`${MINIMISE_CATEGORY_UI_ITEM_PREFIX}-arrow-icon`}
        />
      </div>
    </div>
  );
}
export interface NewHotQuestionProps {
  msgContent?: string;
  hotQuestions: Array<Question>;
  loading?: boolean;
  agentStore?: any;
  initStore?: InitStoreType;
  /** 是否是3x3 */
  isHeadIntent?: boolean;
  /** 是否是orderCard */
  isOrderCard?: boolean;
  /** 如果是3x3，传入一个页数 */
  categoryIndex?: number;
  categoryName: string;
  // 4-FAQ:recommender需要限制行数为3行/减小margintop
  source?: number;
  dialogueId?: string;
  /** 是否是带有live button的Live Agent Recommender */
  hasLiveAgent?: boolean;
  /** 上一个dialogue ID */
  lastDialogueId?: string;
  isHistory?: boolean;
  // 区分埋点的index
  realIndex?: number;
  kbBotId?: number;
  kbBotName?: string;
  /** msgContent前缀 */
  msgPrefix?: string;
  trackingName?: TRACKING_TYPE;
  // 判断 hot question 的类型
  hotQuestionDisplayType?: number;
  // 是否展示查看更多按钮
  showViewMore?: boolean;
  listPos?: number;
  historySessionId?: string;
  historyDialogueId?: string;
  customCallback?: ChatbotUIBasicProps['customCallback'];
  useUIOptimisationRecommender?: boolean;
  useUIOptimisationHotQuestion?: boolean;
  isMinimiseCategoryUI?: boolean;
  isMinimiseCategoryHotQuestion?: boolean;
}

export interface Question {
  id: number;
  text: string;
  intentType: IntentType;
  intentSource: IntentSource;
  queryCallback: any;
  extraTrackingData?: Record<string, string | number>;
  isRepeatedSession?: boolean;
  adhocCategoryName?: string;
  isOtherFrequentlyAsked?: boolean;
}

const pageSize = 3;
@inject(getChildInject('initStore', 'agentStore'))
@observer
class NewHotQuestion extends React.Component<NewHotQuestionProps, { showViewMore?: boolean }> {
  domRef: RefObject<HTMLDivElement>;
  constructor(props: NewHotQuestionProps) {
    super(props);
    this.state = { showViewMore: props.showViewMore };
    this.domRef = createRef();
  }

  handleQuestionClick = (
    question: Question,
    trackingInfo: {
      trackingName: string;
      trackingData: any;
    },
  ) => {
    const { agentStore, isHistory, customCallback = {}} = this.props;

    // 历史消息只上报
    if (isHistory) {
      handleClickHistoryButton();
      return;
    }

    // 在agent过程中，走agent逻辑
    if (agentStore.inAgent) {
      sendBotMessageToAgent({ content: question.text });
    } else {
      // 走ChatBot逻辑
      const params = { ...question.queryCallback, ...customCallback };
      if (question.isOtherFrequentlyAsked) {
        handleClickOtherFrequentlyAsked(question.text, params);
        trackingHandler({
          trackingname: trackingInfo.trackingName,
          trackingdata: trackingInfo.trackingData,
        });
      } else {
        handleClickIntent(question.text, params, (res: any) => {
          trackingHandler({
            trackingname: trackingInfo.trackingName,
            trackingdata: { ...trackingInfo.trackingData, next_chat_dialogue_id: res.dialogue_id },
          });
        });
      }
    }
  };

  render() {
    const {
      msgContent,
      hotQuestions,
      loading,
      categoryIndex = 0,
      categoryName,
      source,
      dialogueId,
      isOrderCard,
      hasLiveAgent,
      lastDialogueId = '',
      isHistory,
      realIndex = 0,
      kbBotId,
      kbBotName,
      msgPrefix,
      trackingName,
      hotQuestionDisplayType,
      listPos,
      historySessionId,
      historyDialogueId,
      initStore,
      useUIOptimisationRecommender,
      useUIOptimisationHotQuestion,
      isHeadIntent,
      isMinimiseCategoryUI,
      isMinimiseCategoryHotQuestion,
    } = this.props;
    const { showViewMore } = this.state;

    const msgContentWithPrefix = `<p><span>${msgPrefix}</span>, ${msgContent}</p>`;

    return (
      <div className={PREFIX} ref={this.domRef}>
        {msgContent && (
          <Msg
            isKbRichText={!!msgPrefix}
            className={clsx('message-content', { 'message-content-ui-optimisation-recommender': useUIOptimisationRecommender })}
            content={msgPrefix ? msgContentWithPrefix : msgContent}
          />
        )}

        <div className={`${PREFIX}-list`}>
          {(showViewMore ? hotQuestions.slice(0, pageSize) : hotQuestions).map((question, index) => {
            if (!question.id && !question.isOtherFrequentlyAsked) {
              return null;
            }

            return (
              <QuestionItem
                key={question.id}
                index={categoryIndex * 3 + index}
                realIndex={realIndex * 3 + index}
                showDivider={index !== hotQuestions.length - 1}
                categoryName={categoryName}
                question={question}
                loading={loading}
                source={source}
                sessionId={initStore?.sessionId || ''}
                dialogueId={dialogueId}
                initialIntent={question?.queryCallback?.initial_intent}
                isOrderCard={isOrderCard}
                hasLiveAgent={hasLiveAgent}
                lastDialogueId={lastDialogueId}
                isHistory={isHistory}
                onClick={this.handleQuestionClick}
                kbBotId={kbBotId}
                kbBotName={kbBotName}
                trackingName={trackingName}
                hotQuestionDisplayType={hotQuestionDisplayType}
                useUIOptimisationHotQuestion={useUIOptimisationHotQuestion}
                useUIOptimisationRecommender={useUIOptimisationRecommender}
                isHeadIntent={isHeadIntent}
                isMinimiseCategoryUI={isMinimiseCategoryUI}
                isMinimiseCategoryHotQuestion={isMinimiseCategoryHotQuestion}
              />
            );
          })}
        </div>
        {showViewMore && hotQuestions.length > pageSize && (
          <Button
            trackingData={{
              trackingsection: TRACKING_SECTION[TRACKING_TYPE.FAQ_RCMD_VIEW_MORE_BUTTON],
              trackingtarget: TRACKING_TARGET[TRACKING_TYPE.FAQ_RCMD_VIEW_MORE_BUTTON],
              trackingdataBasic: {
                chat_session_id: isHistory ? historySessionId : initStore?.sessionId || '',
                chat_dialogue_id: isHistory ? historyDialogueId : dialogueId || '0',
                if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
                bot_id: kbBotId,
                bot_name: kbBotName,
              },
            }}
            text=""
            onClick={() => {
              this.setState({ showViewMore: false }, () => {
                this.domRef.current?.scrollIntoView({ behavior: 'smooth' });
              });
              updateDialogueInfo({ showViewMore: false }, listPos);
            }}
          >
            <>
              <span>{getTrans('key_answer_view_more_btn')}</span>
              <ReactSVG src={IconDown} className="view-more-icon" />
            </>
          </Button>
        )}
      </div>
    );
  }
}

function QuestionItem(props: {
  question: Question;
  index: number;
  showDivider: boolean;
  categoryName: string;
  loading?: boolean;
  onClick: (
    question: Question,
    trackingInfo: {
      trackingName: string;
      trackingData: any;
    }
  ) => void;
  source?: number;
  sessionId?: string;
  dialogueId?: string;
  initialIntent?: string;
  isOrderCard?: boolean;
  hasLiveAgent?: boolean;
  lastDialogueId?: string;
  isHistory?: boolean;
  realIndex: number;
  kbBotId?: number;
  kbBotName?: string;
  trackingName?: TRACKING_TYPE;
  hotQuestionDisplayType?: number;
  useUIOptimisationHotQuestion?: NewHotQuestionProps['useUIOptimisationHotQuestion'];
  useUIOptimisationRecommender?: NewHotQuestionProps['useUIOptimisationRecommender'];
  isHeadIntent?: NewHotQuestionProps['isHeadIntent'];
  isMinimiseCategoryUI?: boolean;
  isMinimiseCategoryHotQuestion?: boolean;
}) {
  const {
    question,
    useUIOptimisationHotQuestion,
    useUIOptimisationRecommender,
    isHeadIntent,
    isOrderCard,
    isMinimiseCategoryUI,
    isMinimiseCategoryHotQuestion,
  } = props;
  // 即是 3 * 3 又是 ui optimisation
  const isHeadIntentUseUIOptimisationHotQuestion = useUIOptimisationHotQuestion && isHeadIntent;
  // 是 category 点击后出的 intent 使用  ui optimisation
  const isCategoryIntent = isUIOptimisationHotQuestion(props.source) && useUIOptimisationHotQuestion;
  const isOrderCardUseUIOptimisationHotQuestion = useUIOptimisationHotQuestion && isOrderCard;
  const isHotQuestionUIOptimisation
    = isHeadIntentUseUIOptimisationHotQuestion || isCategoryIntent || isOrderCardUseUIOptimisationHotQuestion;
  const isIntentHotQuestionGroup2 = props.hotQuestionDisplayType === INTENT_HOT_QUESTION_DISPLAY_TYPE.GROUP2;
  const questionRef = useRef(null);

  let trackingData: any = {
    chat_session_id: props.sessionId,
    content: props.question.text,
    question_content: props.question.text,
    question_id: props.question.id,
    is_order_card: Boolean(props.isOrderCard),
    if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    is_ds_predicted: props.question.intentType === IntentType.Prediction,
    intent_source: props.question.intentSource,
    kb_intent_id: props.question.intentSource === IntentSource.KnowledgeBase ? props.question.id : undefined,
    bot_id: props.kbBotId,
    bot_name: props.kbBotName,
    question_category: props.categoryName,
    location: props.index,
    is_faq: false,
    is_liveAgentRecommender: props.hasLiveAgent ? 1 : 0,
    chat_dialogue_id: props.lastDialogueId || props.dialogueId || '0',
    user_type: agentStore.userTypeName,
    agent_enquiry_category: agentStore.enquiryTypeName,
    is_repeated_session: props.question.isRepeatedSession ? 1 : 0,
    ...(props.question.extraTrackingData || {}),
  };
  if (props.source === 4) {
    trackingData.FAQ = props?.question?.text;
    trackingData.is_faq = true;
    trackingData.chat_dialogue_id = props.dialogueId;
    trackingData.initial_intent = props.initialIntent;
    trackingData.question_text = props?.question?.text;
    trackingData.intent_id = props.question.intentSource === IntentSource.KnowledgeBase ? props.question.id : undefined;
  }
  let trackingName = trackingData.is_faq
    ? TRACKING_TYPE.FAQ_RCMD_HOT_QUESTION
    : props?.trackingName || TRACKING_TYPE.HOTQUESTION;

  if (question.isOtherFrequentlyAsked) {
    trackingName = TRACKING_TYPE.CATEGORY_OTHER_FREQUENTLY_ASKED;
    trackingData = {
      chat_session_id: props.sessionId,
      chat_dialogue_id: props.lastDialogueId || props.dialogueId || '0',
      location: props.index,
    };
  } else if (isMinimiseCategoryHotQuestion) {
    trackingName = TRACKING_TYPE.CATEGORY_HOT_QUESTION;
    trackingData = {
      if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      chat_session_id: props.sessionId,
      chat_dialogue_id: props.lastDialogueId || props.dialogueId || '0',
      question_category: props.categoryName,
      question_content: props.question.text,
      is_ds_predicted: props.question.intentType === IntentType.Prediction,
      kb_intent_id: props.question.intentSource === IntentSource.KnowledgeBase ? props.question.id : undefined,
      bot_id: props.kbBotId,
      bot_name: props.kbBotName,
      location: props.index,
    };
  }

  useEffect(() => {
    addObserver(questionRef.current);

    return () => removeObserver(questionRef.current);
  }, []);

  const TextRender = () => {
    if (isMinimiseCategoryUI) {
      const { text, isOtherFrequentlyAsked, adhocCategoryName } = question;
      return (
        <MinimiseCategoryUIQuestionItem
          text={text}
          isOtherFrequentlyAsked={isOtherFrequentlyAsked}
          adhocCategoryName={adhocCategoryName}
        />
      );
    }

    if (isHotQuestionUIOptimisation) {
      return (
        <div className={`${PREFIX}-item-head-intent-ui-optimisation-content`}>
          <span className={`${PREFIX}-item-head-intent-ui-optimisation-text`}>{props.question.text}</span>
          <span>
            <ReactSVG
              src={UiOptimisationRecommenderRightArrow}
              className={`${PREFIX}-item-head-intent-ui-optimisation-icon`}
            />
          </span>
        </div>
      );
    }

    // 使用 ui optimisation 的也要带上箭头
    if (isIntentHotQuestionGroup2 || useUIOptimisationRecommender) {
      return (
        <div className={`${PREFIX}-item-group2-content`}>
          <span
            className={clsx(
              `${PREFIX}-item-group2-content-text`,
              useUIOptimisationRecommender ? `${PREFIX}-item-group2-content-ui-optimisation-rcmd-text` : '',
            )}
          >
            {props.question.text}
          </span>
          <ReactSVG src={HotQuestionRightArrow} className={`${PREFIX}-item-group2-content-icon`} />
        </div>
      );
    }
    return props.question.text;
  };

  return (
    <div
      ref={questionRef}
      data-trackingname={trackingName}
      data-trackingdata={JSON.stringify(trackingData)}
      data-trackingindex={props.realIndex}
      data-tracking-once={props.hasLiveAgent}
    >
      <div
        className={clsx(
          `${PREFIX}-item`,
          {
            'skeleton-bg': props.loading,
            disabled: props.isHistory,
            loading: props.loading,
            'faq-limit-lines': props.source === 4,
          },
          isIntentHotQuestionGroup2 ? `${PREFIX}-item-group2` : '',
          useUIOptimisationRecommender ? `${PREFIX}-item-ui-optimisation-recommender` : '',
          isHotQuestionUIOptimisation ? `${PREFIX}-item-head-intent-ui-optimisation` : '',
          { [MINIMISE_CATEGORY_UI_ITEM_PREFIX]: isMinimiseCategoryUI },
        )}
        onClick={() => {
          props.onClick(question, {
            trackingName,
            trackingData,
          });
        }}
      >
        {TextRender()}
      </div>
      {props.showDivider && <div className={`${PREFIX}-divider`} />}
    </div>
  );
}

export default NewHotQuestion;
